import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { format } from 'date-fns';
import { useMedia, useMeasure } from 'react-use';
import { Icon, LazyLoadImage, Typography, Button, Modal } from 'ck-ui';
import { getSanityImg } from 'src/sanityClient';
import { usePromotionView } from '../hooks';
import { getPromoName, trackPromotionClick } from '../utils';
import { PortableText } from '../PortableText/PortableText';
import styles from './HallOfFame.module.scss';

const HallOfFameItem = ({ item, isUpComing, title, handle, index, position }) => {
  const [show, setShow] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [ref, { height }] = useMeasure();
  const canHover = useMedia('(hover: hover)');
  const canBeExpanded = !!(item.events?.length > 0 || item.desc?.length > 0);

  const trackClick = () => trackPromotionClick({
    id: handle,
    name: getPromoName(handle, item.trackingID || item.name || item.btnLink || `${title}-${position}-${index}`),
    position: `${handle}-${position}-${index}`
  });

  const content = (isModal) => (
    <div
      className={classnames({ column: isUpComing })}
    >
      <div
        className={classnames(styles.content, { [styles.noHover]: !isUpComing && !canHover, [styles.topZero]: isModal, [styles.mobileExpandedContent]: isExpanded })}
      >
        <div
          className="image-wrapper no-scale"
          style={{ paddingBottom: '125%' }}
        >
          <LazyLoadImage
            src={getSanityImg(item.image).width(400).height(500).dpr(2).url()}
            className={classnames({ [styles.img]: !isUpComing && !isModal })}
            alt={item.name}
          />
        </div>

        <div className={classnames(
          styles.card,
          'is-flex',
          { [styles.lessTop]: !isUpComing,
            [styles.expanded]: canBeExpanded,
            [styles.topZero]: isModal,
            [styles.morePadding]: isModal
          })}
        >
          <a
            className={classnames('is-flex', styles.panel, { [styles.smallPanel]: !isUpComing })}
            href={!canBeExpanded ? item.btnLink : undefined}
            onClick={() => {
              // if there is a link, track the click and return
              if (!canBeExpanded) trackClick();
              if (canHover || !canBeExpanded) return;
              if (isExpanded) {
                setIsExpanded(false);
                return;
              }
              // on mobile, show expanded panel for upcoming events and show a modal for history visitors section
              if (!isUpComing) {
                setShow(true);
              } else {
                setIsExpanded(true);
              }
            }}
          >
            { isUpComing && (
              <div className={classnames(styles.date, 'is-flex')}>
                <Typography variant="heading8" uppercase color="black-bis">
                  {format(new Date(item.date), 'MMM')}
                </Typography>
                <Typography color="black-bis" uppercase variant="heading6">
                  {format(new Date(item.date), 'dd')}
                </Typography>
              </div>
            )}
            <div className={classnames(styles.textWrapper, 'is-flex')}>
              <div className={classnames(styles.text, 'is-flex')}>
                <Typography
                  className={classnames('no-divider', styles.maxWidth, { [styles.smallMaxWidth]: !isUpComing && !isModal })}
                  uppercase
                  color="white"
                  variant={!canHover && !isUpComing && !isModal ? 'heading6' : 'heading5'}
                >
                  {item.name}
                </Typography>
                { isUpComing && (
                  <Typography uppercase variant="heading6" color="grey-light">{item.store}</Typography>
                )}
              </div>
              {canBeExpanded && !isModal && (
                <Typography variant="body-large" className={classnames(styles.isItemsCenter, 'is-flex')}>
                  <Icon color="secondary" className={styles.arrow} name="arrow-up" />
                </Typography>
              )}
            </div>
          </a>
          {!isUpComing && (
            <div className={styles.mt}>{
              item.events.map((event) => (
                <div className={classnames(styles.isItemsCenter, 'is-flex')} key={event._key}>
                  <Typography
                    variant="heading7"
                    color="grey-light"
                    uppercase
                  >
                    {format(new Date(event?.date), 'dd MMM yyyy')}
                  </Typography>
                  <span className={styles.divider} />
                  <Typography
                    variant="heading7"
                    color="white"
                    uppercase
                  >
                    {event.store}
                  </Typography>
                </div>
              ))
            }
            </div>
          )}
          <div
            ref={ref}
            style={{ flex: 1, WebkitLineClamp: Math.round(Math.floor(height) / 21) }}
            className={classnames('portable-text', styles.mt, styles.lineClamp)}
          >
            {item.desc && (
              <PortableText className="has-text-white" content={item.desc} />
            )}
          </div>

          {item.btnText && (
            <div className={classnames('is-flex', styles.mt, styles.btnWrapper)}>
              <Button
                renderAs="a"
                href={item.btnLink}
                className="is-uppercase"
                color="white"
                size={!canHover && !isModal ? undefined : 'medium'}
                onClick={trackClick}
              >
                {item.btnText}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {content(false)}

      {!canHover && !isUpComing && (
        <Modal show={show} onClose={() => setShow(false)} isWhiteCloseBtn>
          {content(true)}
        </Modal>
      )}
    </>
  );
};

HallOfFameItem.propTypes = {
  item: PropTypes.object,
  isUpComing: PropTypes.bool,
  handle: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  position: PropTypes.number.isRequired
};

HallOfFameItem.defaultProps = {
  isUpComing: false,
};

const HallOfFame = ({
  blockHallOfFame,
  handle,
  position,
}) => {
  const upcomingRef = useRef(null);
  const historyVisitorsRef = useRef(null);
  const { heading, celebrities } = blockHallOfFame;
  const today = format(new Date(), 'yyyy-MM-dd');

  useEffect(() => {
    if (upcomingRef.current) {
      // upcoming in-store section slider
      window.theme.initSlider(upcomingRef.current);
    }
  }, []);

  /**
   * sanitize the data for upcoming in-store events and hall of fame visitors sections
   * */
  const { upcomingEvents, historyVisitors } = celebrities.reduce(
    (acc, { celebrity }) => {
      const historyVisitor = {
        _key: celebrity._id,
        name: celebrity.name,
        image: celebrity.image,
        desc: celebrity.desc,
        btnText: celebrity.btnText,
        btnLink: celebrity.btnLink,
        trackingID: celebrity.trackingID,
        events: []
      };
      // use this to decide to push this historyVisitor or not
      let hasEvent = false;
      celebrity.events?.forEach((event) => {
        if (event.date >= today) {
          acc.upcomingEvents.push({
            ...event,
            name: celebrity.name,
            image: event.image || celebrity.image
          });
          hasEvent = true;
        } else if (historyVisitor.events.length <= 4) {
          historyVisitor.events.push(event);
        }
      });
      if (historyVisitor.events.length || !hasEvent) {
        acc.historyVisitors.push(historyVisitor);
      }
      return acc;
    },
    { upcomingEvents: [], historyVisitors: [] }
  );

  usePromotionView(
    upcomingEvents.map((item, index) => ({
      id: handle,
      name: getPromoName(handle, item.trackingID || item.name || item.btnLink || `${heading}-${position}-${index}`),
      creative: getSanityImg(item.image).width(400).height(500).dpr(2).url(),
      position: `${handle}-${position}-${index}`,
    })),
    upcomingRef
  );

  usePromotionView(
    historyVisitors.map((item, index) => ({
      id: handle,
      name: getPromoName(handle, item.trackingID || item.name || item.btnLink || `${heading}-${position}-${index}`),
      creative: getSanityImg(item.image).width(400).height(500).dpr(2).url(),
      position: `${handle}-${position}-${index}`,
    })),
    historyVisitorsRef
  );

  const getHallOfFameSection = (title, isUpComing, items) => (
    <section className="home-section">
      <div className="container is-clipped">
        <div className="home-section-header">
          <h2 className={classnames('heading5 is-uppercase', styles.heading)}>{title}</h2>
        </div>
        <div
          ref={isUpComing ? upcomingRef : historyVisitorsRef}
          className={classnames({ 'columns scrollable is-mobile': isUpComing, [styles.grid]: !isUpComing })}
        >
          {items.map((item, index) => (
            <HallOfFameItem
              key={item._key}
              isUpComing={isUpComing}
              item={item}
              handle={handle}
              position={position}
              index={index}
              title={heading}
            />
          ))}
        </div>
      </div>
    </section>
  );

  return (
    <>
      {/* eslint-disable-next-line no-nested-ternary */}
      {upcomingEvents.length > 0 && getHallOfFameSection('UPCOMING IN-STORE EVENTS', true, upcomingEvents.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)))}
      {historyVisitors.length > 0 && getHallOfFameSection('HALL OF FAME VISITORS', false, historyVisitors)}
    </>
  );
};

HallOfFame.propTypes = {
  blockHallOfFame: PropTypes.object.isRequired,
  handle: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired
};

export { HallOfFame };
